/**
 * A project's Safety documents tab (R162, decision 926; named "Safety" until decision 1037; docs/specs/cloud/deliverables.md §4; help `cloud.safety-documents`): the
 * risk assessment (the company's .xlsm, as it is), the signed JSA, the signed JSP and the hazards KML pilots load in
 * Oz Runways. Staff add versions and press "Show to client" per document, as with reports; a newer version added to a
 * kind already shown is shown in its place. The client sees, per kind, the version shown, and downloads it. The
 * client's KML leaves out the landholder properties unless they are shared for the project (decision 83). Below them,
 * the pre-flight briefs and safety meetings made on Teslyn Mobile (R161, decision 974; `SafetyBriefs.tsx`): read only
 * for staff; the client sees each brief's PDF only while the project's setting says so. Then the Height breach
 * report's entries from Teslyn Field (decision 1017; `HeightBreaches.tsx`): staff only, read only. Decision 1037 (MB,
 * 4 Nov 2026): the tab is for the safety documents; the safety register entries approved for the client are on the
 * client portal's own Safety register page (R220, `ClientSafetyRegisterScreen`), no longer listed here.
 *
 * R191 (decision 1010 item 2): each version of the JSA, the JSP and the risk assessment can be sent to the client for
 * approval ("Send to client for approval"); its state shows under the version (waiting, approved, declined, superseded
 * by a newer version). The client approves the version as it is (decision 926: the .xlsm as it is) on the project's
 * Approvals tab.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  canPortal,
  canSendApproval,
  latestApproval,
  safetyFileProblem,
  sizeText,
  shortDate,
  SAFETY_DOCUMENT_LABEL,
  SAFETY_FILE_TYPES,
  type SafetyDocumentKind,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { ReportsMode } from '../portal/ReportsList.tsx';
import type { PortalProject } from '../portal/api.ts';
import { signoffFileUrl } from '../safety/api.ts';
import { ClientBriefs, StaffBriefs } from './SafetyBriefs.tsx';
import { StaffHeightBreaches } from './HeightBreaches.tsx';
import { loadProjectApprovals, sendApproval, type StaffApproval } from '../approvals/api.ts';
import { ApprovalLine, SendForApproval } from '../approvals/ApprovalParts.tsx';
import {
  addSafetyDocument,
  hazardsKml,
  loadClientSafety,
  loadHazardPack,
  loadSafetyDocuments,
  loadSignedCopies,
  removeSafetyDocument,
  safetyFileUrl,
  setHazardsShown,
  setSafetyShown,
  type ClientSafetyDocument,
  type SafetyDocumentRow,
  type SignedCopy,
} from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

type FileKind = Exclude<SafetyDocumentKind, 'hazards_kml'>;
const FILE_KINDS: readonly FileKind[] = ['risk_assessment', 'jsa', 'jsp'];

const KIND_HINT: Readonly<Record<FileKind, string>> = {
  risk_assessment: 'The company’s Excel risk assessment, added as its .xlsm file, as it is.',
  jsa: 'The signed JSA: the copy DocuSeal signed, or a PDF signed on paper.',
  jsp: 'The signed JSP safety meeting: the copy DocuSeal signed, or a PDF signed on paper.',
};

export interface SafetyServices {
  /** Open an address in a new tab (tests record it). */
  readonly open?: (url: string) => void;
  /** Hand the browser a file to save (tests record it). */
  readonly save?: (fileName: string, text: string, type: string) => void;
  /** Fetch a signed copy's bytes from its 5-minute address. */
  readonly fetchBlob?: (url: string) => Promise<Blob>;
}

const defaultOpen = (url: string): void => {
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) window.location.assign(url);
};

const defaultSave = (fileName: string, text: string, type: string): void => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

const defaultFetch = async (url: string): Promise<Blob> => {
  const r = await fetch(url);
  if (!r.ok) throw new Error('The signed copy could not be fetched from Teslyn Cloud.');
  return r.blob();
};

export function SafetyTab({
  sb,
  project,
  mode,
  roles = [],
  services = {},
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly mode: ReportsMode;
  readonly roles?: readonly string[];
  readonly services?: SafetyServices;
}): ReactElement {
  return mode === 'staff' ? (
    <StaffSafety sb={sb} project={project} roles={roles} services={services} />
  ) : (
    <ClientSafety sb={sb} project={project} services={services} />
  );
}

function ClientSafety({
  sb,
  project,
  services,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly services: SafetyServices;
}): ReactElement {
  const [docs, setDocs] = useState<ClientSafetyDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // R161 (decision 974.6): the briefs' PDFs, when the project's setting shows them
  const [briefCount, setBriefCount] = useState(0);
  // decision 1023: the safety register entries shown to the client
  const reload = useCallback(() => {
    setError(null);
    loadClientSafety(sb, project.id)
      .then(setDocs)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  async function download(d: ClientSafetyDocument): Promise<void> {
    setNotice(null);
    try {
      if (d.kind === 'hazards_kml') {
        const k = await hazardsKml(sb, project.id, project.number);
        (services.save ?? defaultSave)(k.fileName, k.kml, 'application/vnd.google-earth.kml+xml');
      } else (services.open ?? defaultOpen)(await safetyFileUrl(sb, d));
    } catch (e) {
      setNotice(message(e));
    }
  }

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!docs) return <Loading what="Safety documents" />;
  return (
    <div className="stack">
      <p className="body">The safety documents of this survey shared with you, each in its latest version.</p>
      {notice && (
        <p className="notice notice--fail" role="alert">
          {notice}
        </p>
      )}
      {docs.length === 0 && briefCount === 0 ? (
        <p className="body muted">No safety document has been shared yet.</p>
      ) : docs.length === 0 ? null : (
        <ul className="stack" aria-label="Safety documents">
          {docs.map((d) => (
            <li key={d.id} className="panel row row--wrap">
              <div className="grow">
                <p className="body">{SAFETY_DOCUMENT_LABEL[d.kind]}</p>
                <p className="caption muted">
                  {d.kind === 'hazards_kml'
                    ? `The hazards pilots load in Oz Runways, version ${d.pack_version ?? 1}${
                        d.pack_published_at ? ` of ${shortDate(d.pack_published_at)}` : ''
                      }.`
                    : `${d.file_name}${d.size_bytes ? `, ${sizeText(d.size_bytes)}` : ''}. Shared ${shortDate(d.shown_at)}.`}
                </p>
              </div>
              <Button
                variant="quiet"
                onClick={() => void download(d)}
                aria-label={`Download ${SAFETY_DOCUMENT_LABEL[d.kind]}`}
              >
                Download
              </Button>
            </li>
          ))}
        </ul>
      )}
      <ClientBriefs
        sb={sb}
        project={project}
        onLoaded={setBriefCount}
        {...(services.open ? { open: services.open } : {})}
      />
    </div>
  );
}

function StaffSafety({
  sb,
  project,
  roles,
  services,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  readonly services: SafetyServices;
}): ReactElement {
  const [docs, setDocs] = useState<SafetyDocumentRow[] | null>(null);
  const [signed, setSigned] = useState<SignedCopy[]>([]);
  const [pack, setPack] = useState<{ version: number; published_at: string } | null>(null);
  const [approvals, setApprovals] = useState<StaffApproval[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const mayPublish = canPortal(roles, 'safety.publish') && project.status !== 'archived';

  const reload = useCallback(() => {
    setError(null);
    Promise.all([
      loadSafetyDocuments(sb, project.id),
      loadSignedCopies(sb, project.id).catch(() => [] as SignedCopy[]),
      loadHazardPack(sb, project.id).catch(() => null),
      loadProjectApprovals(sb, project.id).catch(() => [] as StaffApproval[]),
    ])
      .then(([d, s, p, a]) => {
        setDocs(d);
        setSigned(s);
        setPack(p);
        setApprovals(a);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  async function run(key: string, what: () => Promise<void>, ok: string): Promise<void> {
    setBusy(key);
    setNotice(null);
    try {
      await what();
      setNotice({ ok: true, text: ok });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  }

  async function addFile(kind: FileKind, file: File): Promise<void> {
    const problem = safetyFileProblem(kind, file.name, file.size);
    if (problem) {
      setNotice({ ok: false, text: problem });
      return;
    }
    await run(
      `add:${kind}`,
      async () => {
        await addSafetyDocument(sb, project, kind, file, file.name);
      },
      `${file.name} was added as a new version of the ${SAFETY_DOCUMENT_LABEL[kind].toLowerCase()}. If an earlier version was shown to the client, this one is shown in its place.`,
    );
  }

  async function addSigned(kind: 'jsa' | 'jsp', s: SignedCopy): Promise<void> {
    await run(
      `signed:${s.id}`,
      async () => {
        const url = await signoffFileUrl(sb, s.id, 'signed');
        const blob = await (services.fetchBlob ?? defaultFetch)(url);
        const name = `${s.title.replace(/[\\/:*?"<>|]/g, '_')}_Signed.pdf`;
        await addSafetyDocument(sb, project, kind, blob, name, s.id);
      },
      `The signed copy of ${s.title} was added.`,
    );
  }

  async function download(d: SafetyDocumentRow): Promise<void> {
    setNotice(null);
    try {
      if (d.kind === 'hazards_kml') {
        const k = await hazardsKml(sb, project.id, project.number);
        (services.save ?? defaultSave)(k.fileName, k.kml, 'application/vnd.google-earth.kml+xml');
      } else (services.open ?? defaultOpen)(await safetyFileUrl(sb, d));
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    }
  }

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!docs) return <Loading what="Safety documents" />;
  const hazards = docs.find((d) => d.kind === 'hazards_kml') ?? null;
  const used = new Set(
    docs
      .filter((d) => !d.removed_at)
      .map((d) => d.signoff_request_id)
      .filter(Boolean),
  );

  const shownLine = (d: { shown: boolean; shown_at: string | null; shown_by_name: string }): string =>
    d.shown && d.shown_at
      ? `Shown to the client since ${shortDate(d.shown_at)}${d.shown_by_name ? ` (${d.shown_by_name})` : ''}.`
      : 'Not shown to the client.';

  return (
    <div className="stack">
      <p className="body">
        The documents the client may see under Safety. Add each new version here; press &quot;Show to
        client&quot; on the version the client should have. Once a document is shown, a newer version you add
        is shown in its place.
        {!project.clientOrgId &&
          ' No client organisation is linked to this project, so the client sees nothing yet.'}
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {FILE_KINDS.map((kind) => {
        const versions = docs.filter((d) => d.kind === kind);
        const copies =
          kind === 'risk_assessment'
            ? []
            : signed.filter(
                (s) => s.document_kind === (kind === 'jsa' ? 'jsa' : 'jsp_meeting') && !used.has(s.id),
              );
        return (
          <section key={kind} className="panel stack" aria-label={SAFETY_DOCUMENT_LABEL[kind]}>
            <h2 className="heading">{SAFETY_DOCUMENT_LABEL[kind]}</h2>
            <p className="caption muted">{KIND_HINT[kind]}</p>
            {versions.length === 0 && <p className="body muted">No version added yet.</p>}
            {versions.map((d) => (
              <div key={d.id} className="row row--wrap">
                <div className="grow">
                  <p className="body">
                    Version {d.version}: {d.file_name}
                    {d.removed_at && <span className="chip chip--warn"> Removed</span>}
                  </p>
                  <p className="caption muted">
                    {d.size_bytes ? `${sizeText(d.size_bytes)}, ` : ''}
                    {d.source === 'signoff' ? 'signed with DocuSeal, ' : ''}added {shortDate(d.created_at)}
                    {d.added_by_name ? ` by ${d.added_by_name}` : ''}. {d.removed_at ? '' : shownLine(d)}
                  </p>
                  {(() => {
                    const a = latestApproval(approvals, d.id);
                    return a ? <ApprovalLine row={a} /> : null;
                  })()}
                </div>
                {!d.removed_at && (
                  <Button
                    variant="quiet"
                    onClick={() => void download(d)}
                    aria-label={`Download version ${d.version}`}
                  >
                    Download
                  </Button>
                )}
                {mayPublish && !d.removed_at && (
                  <Button
                    variant={d.shown ? 'quiet' : 'secondary'}
                    busy={busy === `shown:${d.id}`}
                    onClick={() =>
                      void run(
                        `shown:${d.id}`,
                        () => setSafetyShown(sb, d.id, !d.shown),
                        d.shown
                          ? `Version ${d.version} is no longer shown to the client.`
                          : `Version ${d.version} is shown to the client.`,
                      )
                    }
                    aria-label={`${d.shown ? 'Hide from client' : 'Show to client'}: version ${d.version}`}
                  >
                    {d.shown ? 'Hide from client' : 'Show to client'}
                  </Button>
                )}
                {mayPublish && !d.removed_at && confirm !== d.id && (
                  <Button
                    variant="quiet"
                    onClick={() => setConfirm(d.id)}
                    aria-label={`Remove version ${d.version}`}
                  >
                    Remove
                  </Button>
                )}
                {!d.removed_at && canSendApproval(roles, kind) && project.status !== 'archived' && (
                  <SendForApproval
                    what={`version ${d.version}`}
                    disabledReason={
                      project.clientOrgId ? null : 'Link a client organisation to send it for approval.'
                    }
                    busy={busy === `approval:${d.id}`}
                    onSend={(note) =>
                      void run(
                        `approval:${d.id}`,
                        async () => {
                          await sendApproval(sb, { kind, itemId: d.id, note });
                        },
                        `Version ${d.version} was sent to the client for approval. The client reads it on the project's Approvals tab.`,
                      )
                    }
                  />
                )}
                {confirm === d.id && (
                  <>
                    <span className="caption">The client no longer sees this version.</span>
                    <Button
                      variant="primary"
                      busy={busy === `remove:${d.id}`}
                      onClick={() =>
                        void run(
                          `remove:${d.id}`,
                          () => removeSafetyDocument(sb, d.id),
                          `Version ${d.version} was removed.`,
                        )
                      }
                    >
                      Remove
                    </Button>
                    <Button variant="quiet" onClick={() => setConfirm(null)}>
                      Keep
                    </Button>
                  </>
                )}
              </div>
            ))}
            {mayPublish && (
              <div className="row row--wrap">
                <label className="btn btn--secondary">
                  Add a version…
                  <input
                    type="file"
                    className="sr-only"
                    accept={SAFETY_FILE_TYPES[kind].join(',')}
                    aria-label={`Add a version of the ${SAFETY_DOCUMENT_LABEL[kind].toLowerCase()}`}
                    disabled={busy !== null}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void addFile(kind, f);
                    }}
                  />
                </label>
                {copies.map((s) => (
                  <Button
                    key={s.id}
                    variant="quiet"
                    busy={busy === `signed:${s.id}`}
                    onClick={() => void addSigned(kind as 'jsa' | 'jsp', s)}
                  >
                    Add the signed copy: {s.title}
                  </Button>
                ))}
              </div>
            )}
          </section>
        );
      })}
      <section className="panel stack" aria-label="Hazards KML">
        <h2 className="heading">Hazards KML</h2>
        <p className="caption muted">
          The project’s pilot hazard pack, the one pilots load in Oz Runways, always the latest published. The
          client’s copy leaves out the landholder properties unless &quot;Show the landholder boundaries to
          the client&quot; is on for this project (Map tab).
        </p>
        <p className="body">
          {pack
            ? `Pilot hazard pack version ${pack.version}, published ${shortDate(pack.published_at)}.`
            : 'No pilot hazard pack is published for this project yet (Teslyn Field, Map, "Publish for pilots…"). The client sees nothing here until there is one.'}{' '}
          {hazards ? shownLine(hazards) : 'Not shown to the client.'}
        </p>
        <div className="row row--wrap">
          {pack && (
            <Button
              variant="quiet"
              onClick={() =>
                void download(
                  hazards ??
                    ({ kind: 'hazards_kml', storage_path: null, file_name: '' } as SafetyDocumentRow),
                )
              }
            >
              Download the client’s copy
            </Button>
          )}
          {mayPublish && (
            <Button
              variant={hazards?.shown ? 'quiet' : 'secondary'}
              busy={busy === 'hazards'}
              onClick={() =>
                void run(
                  'hazards',
                  () => setHazardsShown(sb, project.id, !hazards?.shown),
                  hazards?.shown
                    ? 'The hazards KML is no longer shown to the client.'
                    : 'The hazards KML is shown to the client.',
                )
              }
              aria-label={`${hazards?.shown ? 'Hide from client' : 'Show to client'}: hazards KML`}
            >
              {hazards?.shown ? 'Hide from client' : 'Show to client'}
            </Button>
          )}
        </div>
      </section>
      <StaffBriefs
        sb={sb}
        project={project}
        roles={roles}
        {...(services.open ? { open: services.open } : {})}
      />
      {/* decision 1017: the Height breach report's entries, staff only */}
      <StaffHeightBreaches sb={sb} project={project} roles={roles} />
    </div>
  );
}
